import { createSelector } from 'reselect';
import moment from 'moment';

const stateSelector = state => state.billing;

export const fetchingSelector = createSelector(stateSelector, state => state.fetching);
// `state.fetching` is now `{ trips: bool, others: bool }`; expose per-scope
// selectors so each tab can display its own loading marker without being
// triggered by a fetch in progress for the other tab.
export const tripsFetchingSelector = createSelector(fetchingSelector, fetching => !!(fetching && fetching.trips));
export const othersFetchingSelector = createSelector(fetchingSelector, fetching => !!(fetching && fetching.others));
export const listFilterSelector = createSelector(stateSelector, state => state.listFilter);
const listSelector = createSelector(stateSelector, state => state.list);
export const yearSelector = createSelector(stateSelector, state => (state.year == null ? '' : state.year));
export const monthSelector = createSelector(stateSelector, state => (state.month === null ? '' : state.month));

export const dateFilterSelector = createSelector([yearSelector, monthSelector], (year, monthNumber) => {
    if (year) {
        if (typeof monthNumber === 'number') {
            const month = moment()
                .month(monthNumber)
                .format('MM');
            const startDate = `${year}-${month}-01`;
            const lastDayOfMonth = moment(startDate).daysInMonth();
            const endDate = `${year}-${month}-${lastDayOfMonth}`;
            return { startDate, endDate };
        }
        const startDate = `${year}-01-01`;
        const endDate = `${year}-12-31`;
        return { startDate, endDate };
    }
    return undefined;
});

export const profileSelector = createSelector(stateSelector, state => (state.profile == null ? '' : state.profile));

export const activeScopeSelector = createSelector(stateSelector, state => state.activeScope);
export const tabMemorySelector = createSelector(stateSelector, state => state.tabMemory || {});

// Raw filter values (year/month/profile without the empty-string transform the
// display selectors apply). Used by the Filter to compare against tab memory.
export const rawFilterSelector = createSelector(stateSelector, state => ({
    year: state.year,
    month: state.month,
    profile: state.profile,
}));

// Per-scope record of the filter the saga used the last time it fetched data
// for that scope. Used by the Filter to short-circuit refetches.
export const lastFetchedFilterSelector = createSelector(stateSelector, state => state.lastFetchedFilter || {});

const getMonthAndYear = date => {
    const dateMoment = moment(date);
    return [dateMoment.month(), dateMoment.year()];
};

export const tripsSelector = createSelector(
    [listSelector, yearSelector, monthSelector, profileSelector],
    (list, year, month, profile) => {
        if (!list) return list;
        if (!year && !month && !profile) return list.trips;
        return list.trips.filter(item => {
            const [billingMonth, billingYear] = (item.invoice &&
                item.invoice.billingDate &&
                getMonthAndYear(item.invoice.billingDate)) || [null, null];
            return (
                (!year || billingYear === year || item.year === year) &&
                (typeof month === 'number' ? billingMonth === month || item.month === month : true) &&
                (!profile || item.profileId === profile)
            );
        });
    }
);

export const uniqueTripsSelector = createSelector([tripsSelector], tripsList => {
    if (!tripsList) {
        return tripsList;
    }

    const tripsListUniqueMap = tripsList.reduce((map, cur) => {
        if (!map.has(cur.trip.tripId)) {
            map.set(cur.trip.tripId, { ...cur, invoices: [cur.invoice] });
        } else {
            const trip = map.get(cur.trip.tripId);
            trip.invoices.push(cur.invoice);
        }
        return map;
    }, new Map());

    const tripsListUnique = [...tripsListUniqueMap.values()];
    return tripsListUnique;
});

export const othersSelector = createSelector([listSelector, yearSelector, monthSelector], (list, year, month) => {
    if (!list) return list;
    if (!year && !month) return list.others;
    return list.others.filter(item => item.year === year && (typeof month === 'number' ? item.month === month : true));
});
export const oldInvoicesSelector = createSelector(listSelector, state => (state || {}).old || []);

export const ongoingTripsReceiptsSelector = createSelector(stateSelector, state => state.ongoingTripsReceipts);
export const ongoingTripsReceiptsLoadingSelector = createSelector(
    ongoingTripsReceiptsSelector,
    ongoingTripsReceipts => ongoingTripsReceipts.loading
);
export const ongoingTripsReceiptsDataSelector = createSelector(
    ongoingTripsReceiptsSelector,
    ongoingTripsReceipts => ongoingTripsReceipts.data
);
export const ongoingTripsReceiptsByIdSelector = createSelector(
    [ongoingTripsReceiptsDataSelector, (_, tripId) => tripId],
    (data, tripId) => data[tripId] || {}
);
